import React, { useEffect, useMemo, useState } from 'react';
import {
    Box,
    CircularProgress,
    InputAdornment,
    Paper,
    Switch,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Typography,
} from '@material-ui/core';
import SearchIcon from '@material-ui/icons/Search';
import { Card } from 'react-bootstrap';
import request from 'tools/request';
import { Toast } from 'components/shared/Toaster';
import ConfirmationDialog from 'components/ConfirmationDialog';
import { featureFlagLabel, featureFlagCategory, compareFeatureFlags } from 'constants/featureFlags';

/**
 * Every feature flag for one client, each with its own switch. Uses the same bulk endpoint as the
 * Settings > Feature Flags screen, scoped to this client's UUID.
 */
const ClientFeatureFlagsTab = ({ clientUuid, clientName, enabledFlags = [], onChanged }) => {
    const [availableFlags, setAvailableFlags] = useState([]);
    const [loading, setLoading] = useState(false);
    const [savingFlag, setSavingFlag] = useState(null);
    const [search, setSearch] = useState('');
    const [pending, setPending] = useState(null);
    // Local copy so a switch reflects the change right away, before the client is reloaded.
    const enabledKey = (enabledFlags || []).slice().sort().join(',');
    const [enabled, setEnabled] = useState(new Set(enabledFlags));

    useEffect(() => {
        setEnabled(new Set(enabledKey ? enabledKey.split(',') : []));
    }, [enabledKey]);

    useEffect(() => {
        setLoading(true);
        request('clients/feature-flags/available', 'GET')
            .then((res) => setAvailableFlags(res || []))
            .finally(() => setLoading(false));
    }, []);

    const flags = useMemo(() => {
        const term = search.trim().toLowerCase();
        return availableFlags
            .filter((flag) => !term || featureFlagLabel(flag).toLowerCase().includes(term) || flag.toLowerCase().includes(term)
                || featureFlagCategory(flag).toLowerCase().includes(term))
            .sort(compareFeatureFlags);
    }, [availableFlags, search]);

    const applyChange = () => {
        const { flag, enable } = pending;
        setPending(null);
        setSavingFlag(flag);
        request('clients/feature-flags/bulk', 'POST', { flag, action: enable ? 'ENABLE' : 'DISABLE', clientIds: [clientUuid] })
            .then(() => {
                setEnabled((prev) => {
                    const next = new Set(prev);
                    enable ? next.add(flag) : next.delete(flag);
                    return next;
                });
                Toast({ type: 'success', message: `${featureFlagLabel(flag)} ${enable ? 'enabled' : 'disabled'} for ${clientName || 'this client'}.` });
                onChanged && onChanged();
            })
            .finally(() => setSavingFlag(null));
    };

    return (
        <Card className="p-4">
            <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gridGap={16} mb={2}>
                <Box>
                    <Typography variant="h6">Feature Flags</Typography>
                    <Typography variant="body2" color="textSecondary">
                        {enabled.size} of {availableFlags.length} enabled for {clientName || 'this client'}
                    </Typography>
                </Box>
                <TextField
                    variant="outlined"
                    size="small"
                    placeholder="Search feature flags"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    style={{ minWidth: 300 }}
                    inputProps={{ 'aria-label': 'Search feature flags' }}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon color="action" />
                            </InputAdornment>
                        ),
                    }}
                />
            </Box>

            {loading ? (
                <div className="text-center p-4">
                    <CircularProgress />
                </div>
            ) : (
                <TableContainer component={Paper}>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell><strong>Feature</strong></TableCell>
                                <TableCell><strong>Key</strong></TableCell>
                                <TableCell align="center"><strong>Enabled</strong></TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {flags.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={3} align="center">No feature flags match your search.</TableCell>
                                </TableRow>
                            ) : (
                                flags.map((flag, index) => (
                                    <React.Fragment key={flag}>
                                    {(index === 0 || featureFlagCategory(flags[index - 1]) !== featureFlagCategory(flag)) && (
                                        <TableRow>
                                            <TableCell colSpan={3} style={{ background: 'var(--bg-canvas)', fontWeight: 600 }}>
                                                {featureFlagCategory(flag)}
                                            </TableCell>
                                        </TableRow>
                                    )}
                                    <TableRow hover>
                                        <TableCell>{featureFlagLabel(flag)}</TableCell>
                                        <TableCell style={{ color: 'rgba(0, 0, 0, 0.6)', fontFamily: 'monospace' }}>{flag}</TableCell>
                                        <TableCell align="center">
                                            {savingFlag === flag ? (
                                                <CircularProgress size={20} />
                                            ) : (
                                                <Switch
                                                    checked={enabled.has(flag)}
                                                    onChange={() => setPending({ flag, enable: !enabled.has(flag) })}
                                                    color="primary"
                                                    disabled={savingFlag !== null}
                                                    inputProps={{ 'aria-label': `${featureFlagLabel(flag)} enabled` }}
                                                />
                                            )}
                                        </TableCell>
                                    </TableRow>
                                    </React.Fragment>
                                ))
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}

            <ConfirmationDialog
                open={!!pending}
                onClose={() => setPending(null)}
                onConfirm={applyChange}
                title={`${pending?.enable ? 'Enable' : 'Disable'} Feature Flag`}
                message={pending ? `Are you sure you want to ${pending.enable ? 'enable' : 'disable'} "${featureFlagLabel(pending.flag)}" for "${clientName || 'this client'}"?` : ''}
                loading={savingFlag !== null}
            />
        </Card>
    );
};

export default ClientFeatureFlagsTab;
